import React, { useState, useEffect, useRef, Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Container, Row, Col, Form, Button, Spinner } from 'react-bootstrap'
import ReactHtmlParser from 'react-html-parser'
import ContentBlock from 'components/atoms/ContentBlock'
import Typography from 'components/atoms/Typography'
import { upperFirst, phoneMask, accentuationMask } from 'helpers/strings'
import { get } from 'lodash'
import CustomButton from 'components/atoms/CustomButton'
import Pagination from 'components/molecules/Pagination'
import { DateTime } from 'luxon'
import Breadcrumb from 'components/atoms/Breadcrumb'

import * as medicalScheduleActions from 'store/modules/medicalSchedule/actions'
import './AgendaMedica.scss'

function AgendaMedica(props) {
  const dispatch = useDispatch()
  const history = useHistory()
  const myRef = useRef(null)

  const { data } = props
  const { title, description } = data
  const [showResults, setShowResults] = useState(false)
  const [errors, setErrors] = useState(null)
  const [submitObject, setSubmitObject] = useState(null)

  const query = new URLSearchParams(get(history, 'location.search'))
  const [currentPage, setCurrentPage] = useState(query.get('page') || null)

  const citiesList = useSelector((state) => state.medicalSchedule.cities.list)
  const specialtiesList = useSelector((state) => state.medicalSchedule.specialties.list)
  const medicalScheduleData = useSelector((state) => state.medicalSchedule.schedule.data)
  const medicalScheduleLoading = useSelector((state) => state.medicalSchedule.schedule.loading)
  const breadcrumb = get(props, 'data.breadcrumb') || []
  // const medicalScheduleError = useSelector((state) => state.medicalSchedule.schedule.error)

  const loadingCities = citiesList.length === 0
  const loadingSpecialties = specialtiesList.length === 0

  const [showMoreFields, setShowMoreFields] = useState(false)
  const [formValidated, setFormValidated] = useState(false)

  const [selectedCity, setSelectedCity] = useState(parseInt(query.get('cityCode')) || null)
  const [selectedSpecialty, setSelectedSpecialty] = useState(
    query.get('specialtyType') && query.get('specialtyCode')
      ? `${query.get('specialtyType')}:${query.get('specialtyCode')}`
      : null
  )
  const [providerName, setProviderName] = useState(query.get('providerName') || null)
  const [addressNeighborhood, setAddressNeighbourhood] = useState(query.get('addressNeighborhood') || null)

  const checkForms = () => {
    return selectedSpecialty || providerName
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    event.stopPropagation()
    const form = event.currentTarget

    if (form.checkValidity() && checkForms()) {
      const [specialtyType, specialtyCode] = selectedSpecialty ? selectedSpecialty.split(':') : [null, null]
      const selectedSpecialtyItem = selectedSpecialty
        ? specialtiesList.find((element) => element.type === specialtyType && element.code === parseInt(specialtyCode))
        : null
      let temp = {
        cityCode: selectedCity && parseInt(selectedCity),
        cityName: citiesList.find((element) => element.code === parseInt(selectedCity)).name,
        specialtyCode: specialtyCode ? parseInt(specialtyCode) : null,
        specialtyName: selectedSpecialtyItem && selectedSpecialtyItem.description,
        specialtyType: selectedSpecialtyItem && selectedSpecialtyItem.type,
        providerName: providerName && accentuationMask(providerName),
        addressNeighborhood: addressNeighborhood && accentuationMask(addressNeighborhood),
        page: parseInt(query.get('page')) || 0,
      }
      setSubmitObject(temp)
      setCurrentPage(0)
      setErrors(null)
      setShowResults(true)
    } else {
      setErrors('Especialidade/Área de Atuação ou Nome do Médico/Prestador é obrigatório.')
    }

    setFormValidated(true)
  }

  const updateProviderName = (value) => {
    setErrors(null)
    setProviderName(value)
  }

  const updateSelectedSpecialty = (value) => {
    setErrors(null)
    setSelectedSpecialty(value)
  }

  useEffect(() => {
    dispatch(medicalScheduleActions.getCities())
    dispatch(medicalScheduleActions.getSpecialties())
  }, [])

  useEffect(() => {
    if (currentPage !== null) {
      let temp = {
        ...submitObject,
        page: parseInt(currentPage),
      }
      if (myRef.current !== null) {
        myRef.current.scrollIntoView()
      }
      setShowResults(true)
      dispatch(medicalScheduleActions.getMedicalSchedule(temp))
    }
  }, [currentPage, submitObject])

  const renderSearchParamsList = () => {
    let searchParamsArray = [
      { param: 'Cidade', value: get(submitObject, 'cityName') },
      { param: 'Especialidade/Área de Atuação', value: get(submitObject, 'specialtyName') },
      { param: 'Médico/Prestador', value: get(submitObject, 'providerName') },
      { param: 'Bairro', value: get(submitObject, 'addressNeighborhood') },
    ].filter((item) => !!item)
    return searchParamsArray.map((item, index) => (
      <li key={index}>
        {upperFirst(get(item, 'param'))}: {get(item, 'value')}
      </li>
    ))
  }

  const renderPagination = () => {
    if (get(medicalScheduleData, 'schedules') && medicalScheduleData.last_page > 0) {
      const totalPages = (get(medicalScheduleData, 'last_page') || 0) + 1
      return (
        <>
          <Pagination
            className={`d-none d-md-block`}
            totalPages={totalPages}
            paginationOffset={5}
            initialPage={currentPage}
            onChangePage={(page) => setCurrentPage(page - 1)}
            useQueryParams={false}
          />
          <Pagination
            className={`d-block d-md-none`}
            totalPages={totalPages}
            paginationOffset={1}
            initialPage={currentPage}
            onChangePage={(page) => setCurrentPage(page - 1)}
            useQueryParams={false}
          />
        </>
      )
    } else {
      return ''
    }
  }

  const renderResults = (results) => {
    const initialDate = get(results, '[0].date', null)
    return (
      <ul className="medical-agenda-result__list">
        {newDate(null, initialDate, 'first')}
        {listResults(results, initialDate)}
      </ul>
    )
  }

  const newDate = (initialDate, date, index) => {
    const key = index + '-' + date
    return (
      initialDate !== date && (
        <li key={key} className="medical-agenda-result__available-date">
          <p>
            Data disponível: <strong>{DateTime.fromISO(date).toFormat('dd/MM/yyyy')}</strong>
          </p>
        </li>
      )
    )
  }

  const listResults = (results, initialDate) => {
    if (results.length === 0) {
      return (
        <li key="not-found" className="medical-agenda-result__item">
          <p className="medical-agenda-result__notfound">
            Desculpe, não foram encontrados resultados para sua consulta.
          </p>
        </li>
      )
    } else {
      return results.map((item, index) => {
        const dateBlock = newDate(initialDate, get(item, 'date'), index)
        initialDate = get(item, 'date')
        return (
          <Fragment key={index.toString()}>
            {dateBlock}
            <li className="medical-agenda-result__item">
              <p className="medical-agenda-result__doctor">
                <strong>{get(item, 'provider_name').toUpperCase()}</strong>
              </p>
              <p className="medical-agenda-result__address">
                <i className="icone-pin" /> {get(item, 'address_street')}, {get(item, 'address_number')}
                {get(item, 'address_complement') && <>, {get(item, 'address_complement')}</>} -{' '}
                {get(item, 'address_neighborhood')}
              </p>
              <p className="medical-agenda-result__phone">
                <i className="icone-phone" /> ({get(item, 'address_ddd')}) {phoneMask(get(item, 'address_phone_1'))}
              </p>
              {get(item, 'address_phone_2') && (
                <p className="medical-agenda-result__phone">
                  <i className="icone-phone" /> ({get(item, 'address_ddd')}) {phoneMask(get(item, 'address_phone_2'))}
                </p>
              )}
            </li>
          </Fragment>
        )
      })
    }
  }

  const renderSearchResult = () => {
    if (showResults) {
      const totalRows = get(medicalScheduleData, 'total_rows')
      return (
        <>
          <Row>
            <Col xs={12} lg={10}>
              <section className="medical-agenda-result__search-params">
                <Typography variant="h6" color="green-dark-100" cssClass="mb-3 ">
                  Resultados para:
                </Typography>

                <ul className="medical-agenda-result__params">{renderSearchParamsList()}</ul>

                <CustomButton
                  text="Refazer busca"
                  color="primary"
                  iconAlign="right"
                  onClick={() => setShowResults(false)}
                />
                {totalRows > 0 && (
                  <p className="medical-agenda-result__helper-text">
                    Foram encontrados <strong>{totalRows}</strong> resultados. Disponibilidade sujeita a confirmação do
                    consultório.
                  </p>
                )}
              </section>
            </Col>
          </Row>
          {medicalScheduleLoading && (
            <Row className="justify-content-md-center my-5">
              <Col md="auto" className="text-center">
                <Spinner animation="border" />
              </Col>
            </Row>
          )}
          {get(medicalScheduleData, 'schedules') && !medicalScheduleLoading && (
            <Row>
              <Col>
                <section ref={myRef} className="medical-agenda-result__results">
                  {renderPagination()}
                  {renderResults(get(medicalScheduleData, 'schedules'))}
                  {renderPagination()}
                </section>
              </Col>
            </Row>
          )}
        </>
      )
    } else {
      return (
        <Row>
          <Col xs={12} lg={6}>
            <Form noValidate validated={formValidated} className="medical-agenda__form" onSubmit={handleSubmit}>
              <Form.Group controlId="city-select">
                <Form.Label>Cidade</Form.Label>
                <Form.Control
                  as="select"
                  required={true}
                  defaultValue={selectedCity || ''}
                  disabled={loadingCities}
                  onChange={(e) => setSelectedCity(e.target.value)}
                >
                  <option value="" disabled hidden>
                    {loadingCities ? 'Carregando...' : 'Selecione'}
                  </option>
                  {citiesList.map((item, index) => (
                    <option key={index} value={item.code}>
                      {get(item, 'name')}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">Por favor, escolha uma cidade.</Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="specialty-select">
                <Form.Label>Especialidade/Área de Atuação</Form.Label>

                <Form.Control
                  as="select"
                  isInvalid={errors}
                  isValid={checkForms()}
                  value={selectedSpecialty || ''}
                  disabled={loadingSpecialties}
                  onChange={(e) => updateSelectedSpecialty(e.target.value)}
                >
                  <option value="">{loadingSpecialties ? 'Carregando...' : 'Selecione'}</option>
                  {specialtiesList.map((item, index) => (
                    <option key={index} value={`${get(item, 'type')}:${get(item, 'code')}`}>
                      {get(item, 'description')}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">{errors}</Form.Control.Feedback>
              </Form.Group>

              {showMoreFields && (
                <>
                  <Form.Group controlId="doctor-name">
                    <Form.Label>Nome do Médico/Prestador</Form.Label>
                    <Form.Control
                      isInvalid={errors}
                      value={providerName}
                      isValid={checkForms()}
                      type="text"
                      placeholder="Digite o nome do Médico/Prestador"
                      onChange={(e) => updateProviderName(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group controlId="doctor-neighbourhood">
                    <Form.Label>Bairro do Médico/Prestador</Form.Label>
                    <Form.Control
                      type="text"
                      value={addressNeighborhood}
                      placeholder="Digite o nome do bairro do Médico/Prestador"
                      onChange={(e) => setAddressNeighbourhood(e.target.value)}
                    />
                  </Form.Group>
                </>
              )}
              <Button variant="link" onClick={() => setShowMoreFields(!showMoreFields)}>
                {showMoreFields ? 'Menos' : 'Mais'} opções
              </Button>

              <div className="medical-agenda__submit mt-2">
                <CustomButton text="Buscar" type="submit" color="primary" iconAlign="right" />
              </div>
            </Form>
          </Col>
        </Row>
      )
    }
  }

  return (
    <Container fluid="fluid" className="mt-5 medical-agenda section-spacing-first">
      <Container>
        <Row>
          <Col>
            <ContentBlock>
              <Typography variant="h2" color="primary" cssClass="mb-3 title-semibold-lg">
                {title}
              </Typography>

              {description && (
                <Typography variant="span" component="" color="gray-100" cssClass="bloco-conteudo-text mb-4">
                  {ReactHtmlParser(get(description, 'value'))}
                </Typography>
              )}
            </ContentBlock>
          </Col>
        </Row>

        {renderSearchResult()}
      </Container>
    </Container>
  )
}

export default AgendaMedica
